import { INavigationOptions, NavigationEventHandler } from "@kta20-core/interfaces/i-navigation-options";
import { ViewName } from '@kta20-core/interfaces/view-name';

export const ROOT_CHANGING_EVENT:string='KTA20:ROOT_CHANGING_EVENT';

/**
 * Interface implemented by the
 * main navigation manager.
 * Exposes all methods to make
 * view models to request navigation
 * from a view to another.
 */
export interface INavigationManager {

    /**
     * Returns true if the current
     * active view is not the first
     * one on the navigation stack.
     */
    canGoBack():boolean;

    /**
     * Enables the browser alert message when user
     * reloads the page or is about to leave the
     * app by navigating with history buttons.
     * @param enabled
     */
    alertOnLeave(enabled:boolean);

    /**
     * Registers view loader's into the manager by view name.
     * Each view can be registered only once.
     * @param views : list of ViewName's representing views to register in the manager.
     */
    addViews(views: ViewName[]);

    /**
     * Loads a view by name and make it available
     * for navigation. Loading must be happens once
     * only.
     * @param viewName : name of the view used during registration.
     */
    loadView(viewName: string): Promise<void>;

    /**
     * Set the name of the events fired by the  UI component hosting the navigation stack
     * before and after a navigation change.
     * @param didChangeEventName : name of the event raised by the UI component hosting the navigation stack when it has completed the navigation change event.
     * @param willChangeEventName  : name of the event raised by the UI component hosting the navigation stack when it is about to navigate to the new view.
     */
    setEventNames(didChangeEventName: string, willChangeEventName: string): INavigationManager;

    /**
     * Registers a UI component to hosting a navigation stack
     * where the manager will push/pop views.
     * The manager does support navigating into 
     * multiple stacks/nav-host, so you must identify
     * them by a unique name.
     * @param id : unique name of the UI component into the current layout
     * @param navHost : instance of the UI component hosting the navigation stack
     */
    addNavHost(id: string, navHost: any): INavigationManager;

    /**
     * Removes the UI component hosting the navigation stack.
     * @param id  : unique name of the UI component into the current layout
     */
    removeNavHost(id: string): INavigationManager;

    /**
     * Select the given UI component
     * to host the navigation stack from the list 
     * available and pre-registered nav-hosts.
     * Use this method if you have more than one 
     * navigation stack in your layout, and you
     * want to push/pop pages into one of them
     * instead of the default one.
     * @param id : unique name of the UI component into the current layout
     */
    useNavHost(id: string): INavigationManager;

    /**
     * Returns the UI component hosting the 
     * navigation stack currently in use by
     * the manager.
     */
    getNavHost(): any;

    /**
     * Get the active view from the 
     * navigation stack.
     */
    getActive(): Promise<Object>;

    /**
     * Set the root of the navigation stack
     * to the given view according to the 
     * passed navigation option.
     * Setting the root does reset the navigation
     * stack to the given view.
     * @param opts : concrete navigation options
     */
    setRoot(opts: INavigationOptions): Promise<void>;

    /**
     * Add the given view on top of the navigation stack.
     * @param opts : concrete navigation options
     */
    push(opts: INavigationOptions): Promise<void>;

    /**
     * Removes the given view from the top of
     * the navigation stack.
     * @param opts  : concrete navigation options
     * @param done  : callback function to invoke when done
     */
    pop(opts?: INavigationOptions, done?: NavigationEventHandler): Promise<boolean>;


    //=== ATTACH/REMOVE EVENTS TO THE NAV-HOST 

    on(eventName: string, handler: NavigationEventHandler);
    off(eventName: string, handler: NavigationEventHandler);
    onViewWillChange(handler: NavigationEventHandler, viewName: string);
    onViewDidChange(handler: NavigationEventHandler, viewName: string);
    offViewWillChange(handler: NavigationEventHandler, viewName: string);
    offViewDidChange(handler: NavigationEventHandler, viewName: string);

    //=========================================

    getDidChangeEventName();
    getWillChangeEventName();
}